import { guard } from '@qdabra/forms-shared/helpers';
import { attributeValue } from 'func-xml';
import { compose, equals, toLower } from 'ramda';

const ATTR_VALUE = 'data-fv-value';
const ATTR_LAST_VALUE = 'data-fv-last-value';
const ATTR_CONTENTEDITABLE = 'contenteditable';

const CKEDITOR = window.CKEDITOR;
const $ = window.jQuery;

function elementId(cke) {
    return cke.element.getId();
}

function getCkEditor(id) {
    return CKEDITOR.instances[id];
}

function updateRichText(element, elemId, value) {
    try {
        getCkEditor(elemId).setData(value);

        element.setAttribute(ATTR_LAST_VALUE, value);
    } catch (e) {
        console.error('Error updating rich text box:', e);
    }
}

const showHideToolbar = (editor) =>
    $(`#cke_${elementId(editor)}`).toggleClass('hidden-imp', editor.readOnly);

// DOMElement -> Boolean
const isElementReadOnly = compose(
    equals('false'),
    guard(toLower),
    attributeValue(ATTR_CONTENTEDITABLE),
);

/**
 * Enable or disable rich text editor
 * @param {} editorInstance Current text box instance
 */
function enableDisableRichText(editorInstance) {
    const editable = editorInstance.editable();

    if (!editable) {
        // not yet initialized
        return;
    }

    const element = editable.$;

    const isReadOnly = isElementReadOnly(element);

    if (editorInstance) {
        editorInstance.setReadOnly(isReadOnly);
    }
}

// Checks whether the clicked element or an ancestor is a link and attempts to
// open the target URL if so
const tryOpenLink = (targetEl) => {
    const linkTarget = targetEl.closest('a')?.href;

    if (linkTarget) {
        window.open(linkTarget, '_blank');
    }
};

const clickHandler = (evt) => {
    const editor = evt.sender;
    const targetEl = evt.data.getTarget().$;

    // 50408: Allow opening links on read-only editors
    if (editor.isReadOnly()) {
        tryOpenLink(targetEl);
    }
};

function instanceReadyHandler(evt) {
    const editor = evt.editor;

    enableDisableRichText(editor);

    showHideToolbar(editor);

    const editable = editor.editable?.();

    if (editable) {
        editable.attachListener(editable, 'click', clickHandler);
    }
}

function blurHandler() {
    // Update the associated element to the latest value
    this.updateElement();
    // Trigger change event so that FormsViewer handlers can run
    $('#' + elementId(this)).trigger('change');
}

const readOnlyHandler = (evt) => showHideToolbar(evt.editor);

var fvRtbPattern = /^fvCTRL/;

function isFvControl(elemId) {
    return fvRtbPattern.test(elemId);
}

function shouldCleanup(cke) {
    return cke && (cke.name !== elementId(cke) || !cke.element.isVisible());
}

function removeCkEditor(cke) {
    cke.destroy();
}

// Remove rich text editors whose ID doesn't match the associated element ID and those
// attached to elements that are not visible
function cleanupRichTextBoxes() {
    Object.keys(CKEDITOR.instances)
        .filter(isFvControl)
        .map(getCkEditor)
        .filter(shouldCleanup)
        .forEach(removeCkEditor);
}

const baseOptions = {
    on: {
        instanceReady: instanceReadyHandler,
        blur: blurHandler,
        readOnly: readOnlyHandler,
    },
    extraAllowedContent:
        'ins del td th{border*,vertical-align,font*};h1 h2 h3 p span{color,font*,margin*};ul ol li{list-style*}',
    removeButtons: '',
};

const editorOptions = (element) => ({
    ...baseOptions,
    title: element.getAttribute('title') || '',
});

function attachRichText(elemId, element) {
    if (!getCkEditor(elemId)) {
        CKEDITOR.inline(elemId, editorOptions(element));
    }
}

function ensureRichText($baseNode) {
    cleanupRichTextBoxes();

    $baseNode.find('.fv-rich-text[id]:not([id=""])').each(function (_, elem) {
        const elemId = elem.getAttribute('id');
        const value = elem.getAttribute(ATTR_VALUE);
        const editor = getCkEditor(elemId);

        if (editor) {
            enableDisableRichText(editor);
        }

        if (!editor || value !== elem.getAttribute(ATTR_LAST_VALUE)) {
            attachRichText(elemId, elem);

            updateRichText(elem, elemId, value);
        }
    });
}

function richTextManager() {
    return {
        ensureRichText: ensureRichText,
    };
}

export default richTextManager;
